// libraries
import { Component, Inject, OnInit, HostListener, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { FormControl, NgForm } from '@angular/forms';

// moment
import { Moment } from 'moment';
import * as _moment from 'moment';

// reactivity
import { Observable, concat, of, Subject, BehaviorSubject } from 'rxjs';
import { tap, debounceTime, distinctUntilChanged, switchMap, map, finalize, take, filter } from 'rxjs/operators';

// application custom services
import { RecordService } from '../../../../../services/record.service';
import { NotificationService } from '../../../../../services/notification.service';
import { CustomTranslateService } from '../../../../../services/custom-translate.service';
import { ViewService } from '../../../../../services/view.service';
import { LocalStorageService } from '../../../../../services/local-storage.service';
import { NumberService } from '../../../../../services/helpers/number.service';

// application custom class
import { Select } from '../../../../../objects/select';
import { StatusCode } from '../../../../../lists/status-code';

// application contracts
import { IDialogData, ICustomerOption, ISiteOption, IContactOption,
  IDepartmentOption, ITaxCodeOption, IAccountCodeOption, IPeriodOption,
  IDatePeriodOption, ILineItem, IAssignedUserOption, IItemOption, IRelatedModuleData, IPaymentTermOption
} from '../../../../../contracts/recurring-invoice';

import { isEmpty, get, toNumber, toString, find, first, cloneDeep, clone } from 'lodash-es';
import { ClientStoreService } from '../../../../../services/client-store.service';
import { LooseObject } from '../../../../../objects/loose-object';
import { toFormattedNumber } from '../../../../utils/numbers';
import { blank, filled } from '../../../../utils/common';
import { ContextMenuComponent } from '../../../context-menu/context-menu.component';
import { ContextMenuService } from '../../../../../services/context-menu.service';
import { UUID } from 'angular2-uuid';
import { RecurringInvoiceLine } from '../../../../../objects/line-items/recurring-invoice-line';
import { Relate } from '../../../../../objects/relate';
import { EditformComponent } from '../../../editform/editform.component';
import { FormPopup } from '../../../../../objects/centralized-forms/form-popup';

// create moment instance
const moment = (_moment as any).default || _moment;

@Component({
  selector: 'app-recurring-invoices-line-items',
  templateUrl: './recurring-invoices-line-items.component.html',
  styleUrls: ['./recurring-invoices-line-items.component.scss'],
})
export class RecurringInvoicesLineItems implements OnInit {

  @ViewChild(ContextMenuComponent) contextMenuComponent: ContextMenuComponent;

  @Input('customerId') customerId: string = '';

  @Input('recurringInvoiceConfig') recurringInvoiceConfig: LooseObject = {};

  @Output('onLineItemChange') onLineItemChange = new EventEmitter<LooseObject>();

  /**
   * contains the total exempted tax
   *
   * @var {number}
   */
  fltTotalExcludedTaxAmount: number = 0;

  /**
   * contains the total taxed amount
   *
   * @var {number}
   */
  fltTotalTaxAmount: number = 0;

  /**
   * contains the total included tax amount
   *
   * @var {number}
   */
  fltTotalIncludedTaxAmount: number = 0;

  /**
   * contains the total tax adjustment
   *
   * @var {number}
   */
  totalTaxAdjustment: number = 0;

  /**
   * determine the focused index
   *
   * @var {number}
   */
  focusedLineIndex: number = -1;

  /**
   * contains line items
   */
  arLineItems: ILineItem[] = [];

  /**
   * Configured default account code (only present with accounting connected clients)
   *
   * @type {IAccountCodeOption}
   */
  objDefaultAccountCode: IAccountCodeOption;

  /**
   * Configured default tax code (only present with accounting connected clients)
   *
   * @type {ITaxCodeOption}
   */
  objDefaultTaxCode: ITaxCodeOption;

  objDefaultPaymentTerm: IPaymentTermOption;

  bDepartmentTracking: boolean = false;

  selectedLineItems: LooseObject[] = [];

  bValidLineItem: boolean = false;

  /**
   * class constructor
   *
   * @param {MatDialogRef<RecurringInvoicesLineItems>} currentDialog
   * @param {RecordService}                                   recordService
   * @param {NotificationService}                             notificationService
   * @param {CustomTranslateService}                          translationService
   * @param {ViewService}                                     viewService
   * @param {MAT_DIALOG_DATA: IDialogData}                    objData
   */
  constructor(
    public contextMenuService: ContextMenuService,
    protected recordService: RecordService,
    protected notificationService: NotificationService,
    protected translationService: CustomTranslateService,
    protected viewService: ViewService,
    protected localStorage: LocalStorageService,
    protected numberService: NumberService,
    protected clientService: ClientStoreService,
    protected dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.bDepartmentTracking = this.clientService.isDepartmentTracking();
    this.setDefaultCodes();
    this.addLineItem();
  }

  addLineItem(objData: LooseObject = null): void {
    if (objData) {
      let taxCode: ITaxCodeOption|null = this.objDefaultTaxCode;
      let accountCode: IAccountCodeOption|null = this.objDefaultAccountCode;

      if (filled(get(objData, 'account_code_id'))) {
        accountCode = {
          id: get(objData, 'account_code_id'),
          code: get(objData, 'account_code'),
          text: get(objData, 'account_code_name'),
        };
      }

      if (filled(get(objData, 'tax_code_id'))) {
        taxCode = {
          id: get(objData, 'tax_code_id'),
          code: get(objData, 'tax_code'),
          text: get(objData, 'tax_code_name'),
          rate: toFormattedNumber(get(objData, 'tax_rate')),
          name: get(objData, 'tax_code_name', get(objData, 'name'))
        };
      }

      let objLineItem = {
        quantity: get(objData, 'quantity', 1),
        description: objData['description'],
        objDepartment: null,
        objTaxCode: taxCode,
        unitPrice: objData['unit_price'] || 0,
        objAccountCode: accountCode,
        amount: objData['unit_price'] || 0,
        isInvalidDepartment: false,
        isInvalidTaxCode: false,
        isInvalidAccountCode: false,
        stock: objData['current_stock_level'] || 0,
        select_line_id: UUID.UUID(),
        related_products: get(objData, 'related_products'),
        supplier_pricing: get(objData, 'supplier_pricing'),
      } as ILineItem;

      objLineItem = this.appendItemSearch(objLineItem);
      objLineItem.objItem = {
        id: get(objData, 'item_id', get(objData, 'id')),
        code: get(objData, 'item_code', get(objData, 'code')),
        text: get(objData, 'item_name', get(objData, 'name')),
        labor: get(objData, 'labor'),
      } as any;

      objLineItem.objDepartment = {
        id: get(objData, 'department_id'),
        department_name: get(objData, 'department_name'),
        text: get(objData, 'department_name'),
      } as any;

      if (this.focusedLineIndex > -1) {
        const arLines = clone(this.arLineItems);

        arLines.splice(this.focusedLineIndex, 0, objLineItem);

        this.arLineItems = arLines;
      } else {
        this.arLineItems.push(objLineItem);
      }
    }

    this.calculateLineItemsTotal();
  }

  removeLineItem(intIndex: number): void {
    if (intIndex < 0 || this.arLineItems.length <= 1) {
      return;
    }

    if (this.arLineItems.length > intIndex) {
      this.arLineItems.splice(intIndex, 1);
      this.calculateLineItemsTotal();
    }

    this.focusedLineIndex = -1;
  }

  calculateAmount(objLineItem: ILineItem): void {
    let intQuantity: any = objLineItem.quantity || 0;
    let fltUnitPrice: any = objLineItem.unitPrice || 0;

    objLineItem.unitPrice = toFormattedNumber(parseFloat(fltUnitPrice.toString()), {
      currency: true,
      maxDecimalPlaces: 4,
    });
    objLineItem.amount = toFormattedNumber(parseFloat((intQuantity * fltUnitPrice).toString()), {
      currency: true,
    });

    this.calculateLineItemsTotal();
  }

  calculateLineItemsTotal(): void {
    // reset calculations
    let fltTotalTaxAmount: number = 0;
    let fltTotalExcludedTaxAmount: number = 0;

    // loop each line items
    this.arLineItems.forEach((objLineItem) => {
      // calculate excluded tax
      fltTotalExcludedTaxAmount += (toNumber(objLineItem.unitPrice) * objLineItem.quantity);
      // calculate total taxable amount
      if (objLineItem.objTaxCode) {
        const taxRate = toNumber(objLineItem.objTaxCode.rate) / 100;
        const tax = toNumber(objLineItem.amount) * taxRate;

        fltTotalTaxAmount += tax;
      }
    });

    // sets the property values
    this.fltTotalExcludedTaxAmount = toFormattedNumber(fltTotalExcludedTaxAmount, {
      currency: true,
    });

    this.fltTotalTaxAmount = toFormattedNumber(fltTotalTaxAmount, {
      currency: true,
    });

    // calculate total inc tax amount
    this.fltTotalIncludedTaxAmount = this.fltTotalExcludedTaxAmount + (this.fltTotalTaxAmount + toNumber(this.totalTaxAdjustment));

    this.updateLineItems();
  }

  updateLineItems(): void {
    this.onLineItemChange.next({
      line_items: this.formatLineItem(),
      amount_tax: this.fltTotalTaxAmount,
      amount_tax_ex: this.fltTotalExcludedTaxAmount,
      amount_tax_inc: this.fltTotalIncludedTaxAmount,
      tax_adjustment_amount: this.totalTaxAdjustment,
      has_valid_line_items: this.bValidLineItem,
    });
  }

  onItemChange(objLineItem: ILineItem): void {
    let objSelected: IItemOption = objLineItem.objItem;

    if (isEmpty(objSelected)) return;

    objLineItem.unitPrice = this.getPricebookPrice(objSelected);

    // if we have a tax code
    if (! isEmpty(objSelected.tax_code_id)) {
      objLineItem.objTaxCode = {
        id: objSelected.tax_code_id,
        text: objSelected.tax_code_name,
        code: objSelected.tax_code,
        rate: objSelected.tax_rate,
        name: get(objSelected, 'tax_code_name', get(objSelected, 'name'))
      };
    } else {
      objLineItem.objTaxCode = this.objDefaultTaxCode;
    }

    // if we have account code
    if (! isEmpty(objSelected.account_code_id)) {
      objLineItem.objAccountCode = {
        id: objSelected.account_code_id,
        text: objSelected.account_code_name,
        code: objSelected.account_code
      };
    } else {
      objLineItem.objAccountCode = this.objDefaultAccountCode;
    }

    objLineItem.description = objSelected.description || null;

    this.calculateAmount(objLineItem);
  }

  protected formatLineItem(): Array<any> {
    this.bValidLineItem = true;
    // loop each line items data and push to the payload
    return cloneDeep(this.arLineItems).map((objLineItem: ILineItem) => {
      const objItem: IItemOption = objLineItem.objItem;

      if (
        (this.bDepartmentTracking && blank(objLineItem.objDepartment))
        || blank(objLineItem.objAccountCode)
        || blank(objLineItem.objTaxCode)
      ) {
          this.bValidLineItem = false;
      }

      return {
        item_id: get(objItem, 'id', null),
        item_code: get(objItem, 'code', null),
        item_name: get(objItem, 'text', null),
        labor: get(objItem, 'labor'),
        quantity: objLineItem.quantity,
        unit_price: objLineItem.unitPrice,
        description: objLineItem.description,
        tax_code_id: filled(objLineItem.objTaxCode) ? objLineItem.objTaxCode.id : null,
        total_price: objLineItem.amount,
        account_code_id: filled(objLineItem.objAccountCode) ? objLineItem.objAccountCode.id : null,
        department_id: filled(this.bDepartmentTracking) && filled(objLineItem.objDepartment) ? objLineItem.objDepartment.id : null,
        account_code: filled(objLineItem.objAccountCode)? objLineItem.objAccountCode.code : null,
        tax_code: filled(objLineItem.objTaxCode) ? objLineItem.objTaxCode.code : null,
      };
    });
  }

  onLineFocus(index: number): void {
    this.focusedLineIndex = index + 1;
  }

  onLineTotalChange(input: HTMLInputElement, opts: {
    line: ILineItem,
  }): void {
    const adjustment = toFormattedNumber(input.value, {
      currency: true,
    });

    const quantity = toFormattedNumber(opts.line.quantity);
    const pricing = adjustment / quantity;

    opts.line.unitPrice = toFormattedNumber(pricing, {
      currency: true,
      maxDecimalPlaces: 4,
    });

    this.calculateLineItemsTotal();
  }

  onTaxAdjustment(input: HTMLInputElement): void {
    this.totalTaxAdjustment = toFormattedNumber(input.value, {
      currency: true,
    });

    this.calculateLineItemsTotal();
  }

  onGrandTotalChange(input: HTMLInputElement): void {
    const adjustment = toFormattedNumber(input.value, {
      currency: true,
    });

    const totalPrice = this.fltTotalExcludedTaxAmount;
    const totalTax = this.fltTotalTaxAmount;

    this.totalTaxAdjustment = toFormattedNumber((adjustment - (totalPrice + totalTax)), {
      currency: true,
    });
  }

  doSomethingFromContextMenu(event) {
    let copiedLineItemFromLocalStorage = this.contextMenuService.getCopiedLineItem();

    if (event.action == 'paste' && filled(copiedLineItemFromLocalStorage)) {
      let indexCounter = get(event, ['data', 'index']) + 1;
      copiedLineItemFromLocalStorage.forEach(lineItem => {
        this.addLineItem(lineItem);
        this.calculateAmount(this.arLineItems[indexCounter]);
        indexCounter++;
      });
    } else if (event.action == 'copy') {
      let copiedLineItems = [];
      let eventDataLineItem = get(event, ['data', 'line_item'], []);

      if (this.selectedLineItems.length > 0) {
        copiedLineItems = this.selectedLineItems.map(lineItem => {
          let newLineItem = this.formatCopiedLineItem(lineItem);

          return new RecurringInvoiceLine(newLineItem);
        });
      } else if (eventDataLineItem) {
        let newLineItem = this.formatCopiedLineItem(eventDataLineItem);

        copiedLineItems = [new RecurringInvoiceLine(newLineItem)];
      }

      if (filled(copiedLineItems)) {
        this.contextMenuService.setCopiedLineItem(copiedLineItems);
      }
    }
  }

  formatCopiedLineItem(lineItem: LooseObject) {
    let currentLineItem = cloneDeep(lineItem);
    currentLineItem['tax_code'] = get(currentLineItem, ['objTaxCode', 'code']);
    currentLineItem['tax_code_id'] = get(currentLineItem, ['objTaxCode', 'id']);
    currentLineItem['tax_code_name'] = get(currentLineItem, ['objTaxCode', 'name']);
    currentLineItem['tax_rate'] = get(currentLineItem, ['objTaxCode', 'rate']);
    currentLineItem['account_code'] = get(currentLineItem, ['objAccountCode', 'code']);
    currentLineItem['account_code_id'] = get(currentLineItem, ['objAccountCode', 'id']);
    currentLineItem['account_code_name'] = get(currentLineItem, ['objAccountCode', 'text']);
    currentLineItem['item_id'] = get(currentLineItem, ['objItem', 'id']);
    currentLineItem['item_code'] = get(currentLineItem, ['objItem', 'code']);
    currentLineItem['item_name'] = get(currentLineItem, ['objItem', 'text']);
    currentLineItem['labor'] = get(currentLineItem, ['objItem', 'labor']);
    currentLineItem['department_id'] = get(currentLineItem, ['objDepartment', 'id']);
    currentLineItem['department_name'] = get(currentLineItem, ['objDepartment', 'text']);

    return currentLineItem;
  }

  onClickedLineItem($event, attr) {
    this.selectedLineItems = this.contextMenuService.selectLineItem($event, attr, this.selectedLineItems);
  }

  createItem(lineItem: LooseObject): void {
    let itemData = this._compileItemDefaultValue(lineItem);
    this.dialog.open(EditformComponent, new FormPopup('items', {}, itemData))
      .afterClosed()
      .subscribe((response) => {
        if (get(response, 'status') == 'save' && filled(response.data)) {

          lineItem.objItem = filled(lineItem.objItem) ? lineItem.objItem : {};
          lineItem.objItem.id = response.data.id;
          lineItem.objItem.text = response.data.name;
          lineItem.objItem.code = response.data.code;
          lineItem.objItem.labor = response.data.labor;
          lineItem.unitPrice = toFormattedNumber(parseFloat(response.data.unit_price.toString()), {
            currency: true,
            maxDecimalPlaces: 4,
          });
          lineItem.amount = toFormattedNumber(parseFloat((lineItem.quantity * response.data.unit_price).toString()), {
            currency: true,
          });


        }
      });
  }

  protected _compileItemDefaultValue(lineItem: LooseObject): LooseObject {
    let itemsDefaultValue: LooseObject = {
      name: get(lineItem, 'description'),
      code: get(lineItem, 'description'),
      pricing_method: 'fixed_sell_price',
      unit_price: get(lineItem, 'unitPrice'),
    };

    if (filled(lineItem.objTaxCode)) {
      itemsDefaultValue.default_tax_code_id = get(lineItem, 'objTaxCode.id');
      itemsDefaultValue.default_tax_code_text = get(lineItem, 'objTaxCode.name');
    }

    if (filled(lineItem.objAccountCode)) {
      itemsDefaultValue.default_account_code_id = get(lineItem, 'objAccountCode.id');
      itemsDefaultValue.default_account_code_text = get(lineItem, 'objAccountCode.name');
    }

    return itemsDefaultValue
  }

  protected _getRelateRecord(relateModule, terms = null, filter = {}): Observable<Select[]> {
    filter = (filter) ? filter : {};
    if (relateModule === 'payment_terms') {
      filter['is_customer'] = true;
    }
    return this.recordService.getRecordRelate(relateModule, terms, false, false, filter);
  }

  protected appendItemSearch(objLineItem: ILineItem): ILineItem {
    objLineItem._bIsLoadingItems = false;
    objLineItem._objItemTypeahead$ = new Subject<string>();
    objLineItem._arSelectableItems$ = objLineItem._objItemTypeahead$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => objLineItem._bIsLoadingItems = true),
      switchMap((strTerm) => this.recordService.getProductRecordData(strTerm, undefined, this.customerId).pipe(
        tap(() => objLineItem._bIsLoadingItems = false),
        map((arOptions) => arOptions.map((objOption) => ({
          id: objOption.id,
          text: objOption.text,
          code: objOption['code'],
          unit_price: objOption['unit_price'],
          pricebook_unit_price: objOption['pricebook_unit_price'],
          tax_code_id: objOption['tax_code_id'],
          tax_code_name: objOption['tax_code_name'],
          tax_code: objOption['tax_code'],
          tax_rate: objOption['tax_rate'],
          account_code_id: objOption['account_code_id'],
          account_code_name: objOption['account_code_name'],
          department_id: objOption['department_id'],
          department_name: objOption['department_name'],
          account_code: objOption['account_code'],
          description: objOption['description'],
          labor: objOption['labor'],
        })))
      ))
    );

    objLineItem._bIsLoadingDepartments = false;
    objLineItem._objDepartmentTypeahead$ = new Subject<string>();

    objLineItem._arSelectableDepartments$ = objLineItem._objDepartmentTypeahead$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => objLineItem._bIsLoadingDepartments = true),
      switchMap((strTerm) => this._getRelateRecord('departments', strTerm).pipe(
        tap(() => objLineItem._bIsLoadingDepartments = false),
        map((arOptions) => arOptions.map((objOption) => ({
          id: objOption.id,
          text: objOption.text,
        })))
      ))
    );

    objLineItem._bIsLoadingAccountCodes = false;
    objLineItem._objAccountCodeTypeahead$ = new Subject<string>();
    objLineItem._arSelectableAccountCodes$ = objLineItem._objAccountCodeTypeahead$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => objLineItem._bIsLoadingAccountCodes = true),
      switchMap((strTerm) => this._getRelateRecord('account_codes', strTerm).pipe(
        tap(() => objLineItem._bIsLoadingAccountCodes = false),
        map((arOptions) => arOptions.map((objOption) => ({
          id: objOption.id,
          text: objOption.text,
          code: objOption['code'],
        })))
      ))
    );

    objLineItem._bIsLoadingTaxCodes = false;
    objLineItem._objTaxCodeTypeahead$ = new Subject<string>();
    objLineItem._arSelectableTaxCodes$ = objLineItem._objTaxCodeTypeahead$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => objLineItem._bIsLoadingTaxCodes = true),
      switchMap((strTerm) => this._getRelateRecord('tax_codes', strTerm).pipe(
        tap(() => objLineItem._bIsLoadingTaxCodes = false),
        map((arOptions) => arOptions.map((objOption) => ({
          id: objOption.id,
          text: objOption.text,
          code: objOption['code'],
          rate: objOption['rate'],
          name: objOption['name'],
        })))
      ))
    );

    return objLineItem;
  }

  protected getPricebookPrice(objItem: IItemOption): number {
    if (this.customerId && objItem.pricebook_unit_price) {
      return objItem.pricebook_unit_price
    } else {
      return objItem.unit_price;
    }
  }

  protected setDefaultCodes(): void {
    if (filled(get(this.recurringInvoiceConfig.related_data, 'default_tax_code_sale'))) {
      this.objDefaultTaxCode = this.recurringInvoiceConfig.related_data.default_tax_code_sale;
    }
    if (filled(get(this.recurringInvoiceConfig.related_data, 'default_account_code_sale'))) {
      this.objDefaultAccountCode = this.recurringInvoiceConfig.related_data.default_account_code_sale;
    }
  }
}